import { useEffect, useState } from 'react';
import { Link, useSearch } from '@tanstack/react-router';
import { db, reference, type LocalPlace, type LocalStop } from '../lib/localdb.js';
import { subscribe } from '../lib/outbox.js';
import { addressLines, dayLabel, mapHref, text, windowShort } from '../lib/format.js';
import { Chip } from '../components/Controls.js';
import { formatPhone } from '@monrdv/shared';

/**
 * The technician's day.
 *
 * One screen is the app; everything else is reached from it. It answers, in this order: what
 * am I doing now, where is it, and can I get to it with what is in the van.
 *
 * Read entirely from the local database, so it renders identically in a basement with no
 * signal. There is no loading state tied to the network — only the brief read from IndexedDB.
 *
 * Sourced from stops where this person is the assignee **or in the crew**, because the crew
 * table holds the lead as well and membership is the honest test. A visit worked by two people
 * is on both their phones, showing the same state.
 */

type Requirement = { id: string; key: string; name: unknown; equipmentNote: unknown; isHeight: boolean; isConfinedSpace: boolean };

export function TodayScreen() {
  const search = useSearch({ strict: false }) as { date?: string };
  const [stops, setStops] = useState<LocalStop[] | null>(null);
  const [places, setPlaces] = useState<Map<string, LocalPlace>>(new Map());
  const [requirements, setRequirements] = useState<Requirement[]>([]);

  const day = search.date ? new Date(search.date) : new Date();

  useEffect(() => {
    const load = async () => {
      const database = await db();
      const all = await database.getAll('stops');

      const start = new Date(day);
      start.setHours(0, 0, 0, 0);
      const end = new Date(start);
      end.setDate(end.getDate() + 1);

      const forDay = all
        .filter((stop) => {
          if (!stop.startsAt) return false;
          // A cancelled visit is not happening, and a day that lists it is a day that sends
          // somebody to a door for nothing.
          if (stop.state === 'cancelled') return false;
          const at = new Date(stop.startsAt);
          return at >= start && at < end;
        })
        .sort((a, b) => (a.startsAt ?? '').localeCompare(b.startsAt ?? ''));

      setStops(forDay);
      setPlaces(new Map((await database.getAll('places')).map((place) => [place.placeKey, place])));
      setRequirements(await reference<Requirement[]>('accessRequirements', []));
    };

    void load();
    // The outbox changing means a local state change — a transition, a capture — so the list
    // reflects it without waiting for a sync.
    return subscribe(() => void load());
  }, [search.date]);

  const isToday = new Date().toDateString() === day.toDateString();
  const remaining = stops?.filter((stop) => stop.state !== 'done' && stop.state !== 'not_done').length ?? 0;

  return (
    <div className="today">
      <header className="today__head">
        <h1>{isToday ? "Aujourd'hui" : dayLabel(day)}</h1>
        <p className="today__count">
          {stops === null
            ? ''
            : stops.length === 0
              ? 'Aucun arrêt planifié'
              : `${remaining} de ${stops.length} ${stops.length === 1 ? 'arrêt' : 'arrêts'}`}
        </p>
      </header>

      {stops !== null && stops.length === 0 && (
        <div className="empty">
          <p>Rien de planifié pour cette journée.</p>
          <Link to="/jour" search={{ date: new Date().toISOString().slice(0, 10) }} className="empty__action">
            Revenir à aujourd’hui
          </Link>
        </div>
      )}

      <ol className="stoplist">
        {stops?.map((stop) => (
          <li key={stop.id}>
            <StopCard stop={stop} place={stop.address ? places.get(stop.address.placeKey) ?? null : null} requirements={requirements} />
          </li>
        ))}
      </ol>
    </div>
  );
}

/**
 * One stop on the day list.
 *
 * Five things are on the card because they change what the technician does **before
 * knocking** — the access note, the access requirements, an open hazard, the unit number, and
 * whether anyone has been here before. Everything else is inside.
 */
function StopCard({
  stop,
  place,
  requirements,
}: {
  stop: LocalStop;
  place: LocalPlace | null;
  requirements: Requirement[];
}) {
  const phone = stop.phones[0];
  const map = mapHref(stop.address);
  const { primary, secondary } = addressLines(stop.address);

  const hazards = place?.hazards ?? [];
  const chips = (place?.access ?? [])
    .map((chip) => requirements.find((requirement) => requirement.id === chip.requirementId))
    .filter((requirement): requirement is Requirement => Boolean(requirement));
  const beenHere = (place?.history.length ?? 0) > 0;
  const assets = place?.assets ?? [];

  return (
    <article className={`stop stop--${stop.state}`}>
      <Link to="/arret/$stopId" params={{ stopId: stop.id }} className="stop__main">
        <div className="stop__when">
          <span className="stop__time tnum">{windowShort(stop)}</span>
          <StateBadge state={stop.state} etaAt={stop.etaAt} />
          {stop.pending && <span className="badge badge--pending">Non envoyé</span>}
        </div>

        <h2 className="stop__customer">{stop.customer.name}</h2>
        {stop.customer.organization && <p className="stop__org">{stop.customer.organization}</p>}

        <p className="stop__address">
          {primary}
          <span>{secondary}</span>
        </p>

        {stop.services.length > 0 && (
          <p className="stop__services">{stop.services.map((service) => text(service.name)).join(' · ')}</p>
        )}

        {/*
          An open safety finding at this dwelling or on this asset. Read before the door opens,
          not after — with the same weight as the access note, because it decides what comes out
          of the van and sometimes whether the technician goes in at all.
        */}
        {hazards.length > 0 && (
          <p className="stop__hazard" role="alert">
            <span className="label">Danger</span>
            {hazards[0]!.note}
            {hazards.length > 1 && <span className="more">+{hazards.length - 1}</span>}
          </p>
        )}

        {/* Copper-accented because it is the field's single most useful piece of text: a code
            nobody read is a wasted trip. */}
        {stop.address?.note && (
          <p className="stop__access">
            <span className="label">Accès</span>
            {stop.address.note}
          </p>
        )}

        {chips.length > 0 && (
          <div className="stop__chips">
            {chips.map((chip) => (
              <Chip key={chip.id} tone={chip.isHeight || chip.isConfinedSpace ? 'warm' : 'neutral'}>
                {text(chip.name)}
                {chip.equipmentNote ? ` · ${text(chip.equipmentNote)}` : ''}
              </Chip>
            ))}
          </div>
        )}

        <div className="stop__footer">
          {beenHere && (
            <span className="stop__prior">
              Déjà venu · {place!.history.length} visite{place!.history.length > 1 ? 's' : ''}
              {assets.length > 0 && ` · ${assets.length} équipement${assets.length > 1 ? 's' : ''}`}
            </span>
          )}
          {Number(stop.job.balance) > 0 && <span className="stop__balance tnum">Solde {Number(stop.job.balance).toFixed(2)} $</span>}
        </div>
      </Link>

      <div className="stop__actions">
        {map && (
          <a className="stop__action" href={map} aria-label={`Itinéraire vers ${primary}`}>
            <PinGlyph />
            <span>Trajet</span>
          </a>
        )}
        {phone && (
          <a className="stop__action" href={`tel:${phone.normalized}`} aria-label={`Appeler ${formatPhone(phone.raw)}`}>
            <PhoneGlyph />
            <span>Appeler</span>
          </a>
        )}
      </div>
    </article>
  );
}

/** Where the technician is, in one word. Derived from timestamps, never stored. */
export function StateBadge({ state, etaAt }: { state: LocalStop['state']; etaAt?: string | null }) {
  const labels: Record<LocalStop['state'], string> = {
    scheduled: '',
    en_route: 'En route',
    delayed: 'En retard',
    arrived: 'Sur place',
    working: 'En cours',
    done: 'Terminé',
    not_done: 'Non fait',
    cancelled: 'Annulé',
  };
  if (!labels[state]) return null;
  const eta = state === 'delayed' && etaAt
    ? ` · ${new Date(etaAt).toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' })}`
    : '';
  return <span className={`badge badge--${state}`}>{labels[state]}{eta}</span>;
}

function PinGlyph() {
  return (
    <svg width="22" height="22" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
      <path d="M8 14s4.5-4.2 4.5-7.5a4.5 4.5 0 1 0-9 0C3.5 9.8 8 14 8 14z" />
      <circle cx="8" cy="6.4" r="1.6" />
    </svg>
  );
}

function PhoneGlyph() {
  return (
    <svg width="22" height="22" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
      <path d="M3.5 2.5h2l1 3-1.5 1a7 7 0 0 0 3.5 3.5l1-1.5 3 1v2a1 1 0 0 1-1 1A10.5 10.5 0 0 1 2.5 3.5a1 1 0 0 1 1-1z" />
    </svg>
  );
}
